import { useCallback } from "react";

import { FormGroup, InputGroup, TextArea } from "@blueprintjs/core";
import Select from "react-select";

import { useWorkspace } from "@/components/helpers/custom-hooks/use-workspace";
import { Box, Flex } from "@/components/layout/flexbox";
import {
  action,
  state,
} from "@/components/pages_logged_in/roleplays/details/scenario/edit/useEditScenario";
import { DifficultyTag } from "@/components/pieces/roleplays/DifficultyTag";
import {
  TagItemInput,
  TagsInput,
} from "@/components/pieces/roleplays/TagInput";
import { LIGHTEST_BACKGROUND, SUBTEXT_COLOR } from "@/css/constants";
import { ScenarioDifficulty } from "@/graphql";

export function BasicTab({
  state,
  dispatch,
}: {
  state: state;
  dispatch: (action: action) => void;
}) {
  const workspace = useWorkspace();
  const allowedEditTags = !!workspace;

  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: "setName", name: event.target.value });
    },
    [dispatch],
  );
  const handleDescriptionChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch({ type: "setDescription", description: event.target.value });
    },
    [dispatch],
  );
  const handleDifficultyChange = useCallback(
    (value: { value: ScenarioDifficulty }) => {
      dispatch({ type: "setDifficulty", difficulty: value.value });
    },
    [dispatch],
  );
  const handleTagsChange = useCallback(
    (tags: TagItemInput[]) => {
      dispatch({ type: "setTags", payload: tags });
    },
    [dispatch],
  );
  return (
    <Flex
      flexDirection="column"
      p={24}
      pb={12}
      backgroundColor={LIGHTEST_BACKGROUND}
      className="flex-auto"
    >
      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Scenario Name
          </Box>
        }
      >
        <InputGroup large value={state.name} onChange={handleNameChange} />
      </FormGroup>
      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Description
          </Box>
        }
      >
        <TextArea
          value={state.description}
          onChange={handleDescriptionChange}
          fill={true}
        />
      </FormGroup>
      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Difficulty
          </Box>
        }
        labelInfo={
          <Box fontSize={14} fontWeight={400} color={SUBTEXT_COLOR}>
            Sets user expectations only. Changing difficulty does not alter AI
            behavior.
          </Box>
        }
      >
        <Box mt={1}>
          <Select
            options={Object.values(ScenarioDifficulty).map((value) => ({
              value,
            }))}
            value={{ value: state.difficulty }}
            onChange={(option) => {
              if (option?.value) {
                handleDifficultyChange({ value: option.value });
              }
            }}
            isSearchable={false}
            formatOptionLabel={(option) => {
              if (option?.value) {
                return <DifficultyTag difficulty={option.value} />;
              }
              return null;
            }}
          />
        </Box>
      </FormGroup>
      {allowedEditTags && (
        <FormGroup label="Tags" labelInfo="(optional)">
          <TagsInput
            menuPlacement="top"
            initValue={state.tags || []}
            onChange={(e) => {
              handleTagsChange(e as TagItemInput[]);
            }}
          />
        </FormGroup>
      )}
    </Flex>
  );
}
